import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { gql, useLazyQuery, useQuery } from '@apollo/client';
import cx from 'classnames';
import { faChevronLeft, faChevronRight, faLevelUpAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PersonCard from '../components/PersonCard';
import Agent from '../models/Agent';
import { useAmplitude } from '../amplitude/amplitudeContext';
import Button from '../components/Button';
import { ReactComponent as Enter } from '../assets/icons/enter.svg';
import { ReactComponent as Search } from '../assets/icons/search.svg';
import { ReactComponent as FlagCa } from '../assets/icons/flag_ca.svg';
import { ReactComponent as FlagUs } from '../assets/icons/flag_us.svg';
import InputGroup from '../components/InputGroup';
import styles from './Agents.module.scss';
import { Pagination } from '../models/Pagination';
import { SortInput } from '../models/SortInput';
import useWindowSize from '../hooks/useWindowSize';
import { states } from '../lib/states';
import { SearchType } from '../models/SearchType';
import FormInput, { DropdownOptionProps } from '../components/FormInput';
import { ZeroWidthSpace } from '../components/ZeroWidthSpace';
import { provinces, statesAndProvinces } from '../lib/provinces';
import { useQueryState } from '../hooks/useQueryState';

export interface AgentsProps {
  brokers?: boolean;
}

interface SearchResult {
  agents: Agent[];
  count: number;
}

const SearchResultFragment = gql`
  fragment SearchResultFragment on SearchResult {
    agents {
      activeLocations {
        city
        state
      }
      asab
      bio
      cityState
      email
      facebook
      firstName
      id
      lastName
      linkedIn
      phoneNumber
      photo
      state
      stateBroker
      preferredName
      title
      twitter
      website
    }
    count
  }
`;

const FeaturedAgents = gql`
  ${SearchResultFragment}
  query FeaturedAgents($country: String!, $searchType: SearchType!) {
    featuredAgents(country: $country, searchType: $searchType) {
      ...SearchResultFragment
    }
  }
`;

const LocationSuggestions = gql`
  query LocationSuggestions($term: String!, $country: String) {
    locationSuggestions(term: $term, country: $country)
  }
`;

const NameSuggestions = gql`
  query NameSuggestions($term: String!, $country: String) {
    nameSuggestions(term: $term, country: $country)
  }
`;

interface SearchQueryVariables {
  country?: string | null;
  location?: string | null;
  name?: string | null;
  pagination?: Pagination | null;
  searchType: SearchType;
  sort?: SortInput | null;
}

const SearchQuery = gql`
  ${SearchResultFragment}
  query SearchQuery($country: String, $location: String, $name: String, $pagination: Pagination, $searchType: SearchType!, $sort: Sort) {
    search(country: $country, location: $location, name: $name, pagination: $pagination, searchType: $searchType, sort: $sort) {
      ...SearchResultFragment
    }
  }
`;

const AgentCount = gql`
  query AgentCount {
    agentCounts {
      brokers
      countries {
        ca
        us
      }
    }
  }
`;

const Agents: FC<AgentsProps> = ({ brokers }) => {
  const { t } = useTranslation();

  const [country, setCountry] = useQueryState('country', document.getElementById('sidx-agent-directory')?.getAttribute('default-country')?.toUpperCase() || 'US');
  const [pageString, setPage] = useQueryState('page');
  const page = parseInt(pageString || '1', 10);
  const [region, setRegion] = useQueryState('region');
  const [sort, setSort] = useQueryState('sort');
  const [actualName, setActualName] = useQueryState('name');
  const [actualLocation, setActualLocation] = useQueryState('city');

  const [location, setLocation] = useState(actualLocation);
  const [name, setName] = useState(actualName);
  useEffect(() => {
    setName(actualName);
    setLocation(actualLocation);
  }, [setName, actualName, setLocation, actualLocation]);

  const searchExecuted = Boolean(location || name || region);

  const changePage = (pageNum: number) => {
    window.scrollTo({ top: 0 });
    setPage(pageNum <= 1 ? null : pageNum.toString());
  };

  const searchDebounceTimeout = useRef<NodeJS.Timeout>();
  useEffect(() => {
    if (searchDebounceTimeout.current) {
      clearTimeout(searchDebounceTimeout.current);
    }
    searchDebounceTimeout.current = setTimeout(() => {
      setActualLocation(location);
      setActualName(name);
    }, 600);
  }, [name, location, setActualLocation, setActualName]);

  const [agentsPerPage, setAgentsPerPage] = useState(24);
  useEffect(() => {
    const defaultAgentCount = brokers ? 12 : 16;
    setAgentsPerPage(!searchExecuted ? defaultAgentCount : 24);
  }, [name, location, searchExecuted, brokers]);

  const [queryLocationSuggestions, { data: locationSuggestionData, loading: locationSuggestionLoading }] = useLazyQuery<{ locationSuggestions: string[] }, { term: string; country?: string }>(
    LocationSuggestions,
    {
      variables: { term: location, country },
    }
  );
  useEffect(() => {
    if (location) {
      queryLocationSuggestions();
    }
  }, [location, queryLocationSuggestions]);
  const locationSuggestions = locationSuggestionData?.locationSuggestions;
  const locationSuggestionsMemo = useMemo((): undefined | DropdownOptionProps[] => {
    if (brokers) {
      return (country === 'CA' ? provinces : states).map((s) => ({
        value: s.abbreviation,
        label: s.name,
      }));
    }

    // eslint-disable-next-line consistent-return
    if (!location || locationSuggestionLoading) return;

    return locationSuggestions?.map((suggestion) => ({ label: suggestion, value: suggestion }));
  }, [brokers, country, location, locationSuggestionLoading, locationSuggestions]);

  const [queryFeaturedAgents, { data: featuredAgentsData, loading: featuredAgentsLoading }] = useLazyQuery<{ featuredAgents: SearchResult }, { country: string; searchType: SearchType }>(
    FeaturedAgents,
    {
      variables: { country, searchType: brokers ? SearchType.BROKER : SearchType.AGENT },
      errorPolicy: 'all',
    }
  );
  useEffect(() => {
    if (!actualLocation && !actualName && !region) {
      queryFeaturedAgents();
    }
  }, [actualName, actualLocation, brokers, country, region, queryFeaturedAgents]);

  const [querySearch, { data, error, loading }] = useLazyQuery<{ search: SearchResult }, SearchQueryVariables>(SearchQuery, {
    variables: {
      searchType: brokers ? SearchType.BROKER : SearchType.AGENT,
      name: actualName || null,
      location: (brokers ? region : actualLocation) || null,
      country,
      pagination: new Pagination(agentsPerPage, (page - 1) * agentsPerPage),
      sort: sort ? new SortInput(sort) : null,
    },
    errorPolicy: 'all',
  });

  useEffect(() => {
    if (actualLocation || region || actualName) {
      querySearch();
    }
  }, [actualName, actualLocation, brokers, country, agentsPerPage, page, region, sort, querySearch]);

  const [queryNameSuggestions, { data: nameSuggestionsData }] = useLazyQuery<{ nameSuggestions: string[] }, { country?: string; term: string }>(NameSuggestions, {
    variables: { term: name, country },
    errorPolicy: 'ignore',
  });
  useEffect(() => {
    if (name) {
      queryNameSuggestions();
    }
  }, [name, queryNameSuggestions]);

  /**
   * If the the state or the country change, make sure the state is still valid for the selected country
   */
  useEffect(() => {
    if (country && region) {
      const index = statesAndProvinces.findIndex((s) => s.abbreviation === region);
      if (!~index) {
        setRegion(null);
      }
    }
  }, [country, region, setRegion]);

  const { data: countsData, loading: countsLoading } = useQuery<{ agentCounts: { brokers: number; countries: { [country: string]: number } } }>(AgentCount);

  const [, width] = useWindowSize();

  /**
   * Pagination logic
   */
  const [maxPages, setMaxPages] = useState(20);
  useEffect(() => {
    if (width > 1030) {
      setMaxPages(20);
    } else if (width > 730) {
      setMaxPages(15);
    } else if (width > 580) {
      setMaxPages(10);
    } else {
      setMaxPages(5);
    }
  }, [width]);
  const [numberOfPages, setNumberOfPages] = useState(0);
  const [numberOfResults, setNumberOfResults] = useState(0);
  const rawCount = data?.search.count;
  useEffect(() => {
    if (rawCount) {
      setNumberOfPages(Math.ceil(rawCount / agentsPerPage));
      setNumberOfResults(rawCount);
    }
  }, [rawCount, agentsPerPage]);

  useEffect(() => {
    setPage(null);
  }, [actualLocation, actualName, country, region, setPage]);

  const { logAmplitudeEvent } = useAmplitude();

  const brokersToggleCallback = useCallback(() => {
    logAmplitudeEvent(brokers ? 'toggle to brokers view' : 'toggle to agents view');
    setPage('1');
    setActualLocation('');
    setActualName('');
    setRegion(null);
    setSort(null);
  }, [brokers, logAmplitudeEvent, setPage, setActualLocation, setActualName, setRegion, setSort]);

  return (
    <>
      <div className={styles.searchContainer}>
        <h1>
          {countsLoading || !countsData ? (
            <ZeroWidthSpace />
          ) : (
            `${t('Search')} ${(brokers ? countsData.agentCounts.brokers : countsData.agentCounts.countries[country?.toLowerCase() || 'us']).toLocaleString()} ${
              brokers ? t('eXp Managing Brokers') : `${country === 'US' ? 'U.S.' : 'Canadian'} ${t('eXp Agents')}`
            }`
          )}
        </h1>
        <div className={styles.inputs}>
          <InputGroup className={styles.input}>
            <FormInput
              className={styles.country}
              value={country}
              options={[
                { value: 'US', label: <FlagUs /> },
                { value: 'CA', label: <FlagCa /> },
              ]}
              onChange={(val) => {
                logAmplitudeEvent('selects the country flag toggle in the Location box');
                setCountry(val);
              }}
              data-test="country-dropdown"
              dropdown
            />
            <FormInput
              className={cx(styles.location, { [styles.dropdown]: brokers })}
              clearButton={!brokers}
              defaultToFirstOption={!brokers}
              dropdown={brokers}
              onBlur={() => logAmplitudeEvent(`types in the ${brokers ? 'brokers' : 'agents'} location search box`)}
              onChange={(v) => {
                if (brokers) {
                  setRegion(v);
                } else {
                  setLocation(v);
                }
              }}
              optionIconRight={
                <>
                  <span>Select</span>
                  <Enter />
                </>
              }
              options={locationSuggestionsMemo}
              // eslint-disable-next-line no-nested-ternary
              placeholder={brokers ? (country === 'CA' ? t('Province') : t('State')) : t('Location')}
              value={(brokers ? region : location) || ''}
              data-test={brokers ? 'state-dropdown' : 'location-input'}
            />
          </InputGroup>
          <FormInput
            className={styles.input}
            icon={Search}
            onChange={(value) => {
              setName(value);
            }}
            onBlur={() => logAmplitudeEvent('types in the name search box')}
            placeholder={t('Name')}
            options={nameSuggestionsData?.nameSuggestions?.map((suggestion) => ({ label: suggestion, value: suggestion }))}
            value={name || ''}
            data-test="name-input"
            clearButton
          />
        </div>
        <div className={styles.gradient} />
        <div className={styles.x} />
        <Link
          onClick={brokersToggleCallback}
          className={styles.brokerLink}
          to={{ pathname: brokers ? '/' : '/brokers', search: new URLSearchParams({ country }).toString() }}
          data-test="broker-toggle"
        >
          {brokers ? t('Search Agents') : t('Search Managing Brokers')}
        </Link>
      </div>
      <div className={styles.main}>
        {data?.search.count === 0 ? (
          <div className={styles.noResults}>
            {error ? (
              <strong>An error occurred. Please try again later</strong>
            ) : (
              <>
                <strong>{t("We couldn't find any matches for your search.")}</strong>
                <ul>
                  {['Check for typos or misspelled words', 'Try a different search term', "Don't give up! We know we can find an eXp agent that will meet your needs!"].map((i) => (
                    <li key={i}>
                      <FontAwesomeIcon icon={faLevelUpAlt} />
                      {t(i)}
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        ) : (
          <>
            <div className={cx(styles.summary, { [styles.sortDropdownActive]: searchExecuted || width > 700 })}>
              {searchExecuted && !loading && data?.search.count && (
                <span className={styles.count}>
                  {data.search.count >= 10000 ? `${data.search.count.toLocaleString()}+` : data.search.count.toLocaleString()} {brokers ? t('eXp Managing Brokers') : t('eXp Agents')}
                </span>
              )}
              {!searchExecuted && featuredAgentsData?.featuredAgents.agents.length && (
                <span className={styles.count}>
                  <span className={styles.blue}>{`${featuredAgentsData.featuredAgents.agents.length} ${t('Featured')} `}</span>
                  {brokers ? t('eXp Managing Brokers') : `${country} ${t('eXp Agents')}`}
                </span>
              )}
              {(!data?.search.agents.length || !featuredAgentsData?.featuredAgents.agents.length || loading || featuredAgentsLoading) && <ZeroWidthSpace />}
              {searchExecuted && (
                <FormInput
                  className={styles.dropdown}
                  inputMode="none"
                  onChange={(v) => {
                    if (!v || v === 'best') {
                      setSort(null);
                      return;
                    }

                    if (brokers) {
                      logAmplitudeEvent('selects a sort option for the Broker results');
                    } else {
                      logAmplitudeEvent('selects a sort option for the Agent results');
                    }

                    setSort(v);
                  }}
                  options={[
                    { value: 'best', label: t('Best Match') },
                    { value: 'firstName-ASCENDING', label: `${t('First Name')} ${t('A - Z')}` },
                    { value: 'firstName-DESCENDING', label: `${t('First Name')} ${t('Z - A')}` },
                    { value: 'lastName-ASCENDING', label: `${t('Last Name')} ${t('A - Z')}` },
                    { value: 'lastName-DESCENDING', label: `${t('Last Name')} ${t('Z - A')}` },
                  ]}
                  value={sort || 'best'}
                  data-test="sort-dropdown"
                  dropdown
                />
              )}
            </div>
            <div className={styles.grid}>
              {(((name || location || searchExecuted) && !data?.search.agents.length) || (!(name || location || searchExecuted) && !featuredAgentsData?.featuredAgents.agents.length)) &&
                // eslint-disable-next-line react/no-array-index-key
                new Array(agentsPerPage).fill(null).map((_, i) => <PersonCard key={i} />)}
              {searchExecuted && data?.search.agents.map((agent) => <PersonCard id={agent.id} featured={!!(featuredAgentsData && !searchExecuted)} key={agent.id} />)}
              {!searchExecuted && featuredAgentsData?.featuredAgents?.agents.map((agent) => <PersonCard id={agent.id} featured={!!(featuredAgentsData && !searchExecuted)} key={agent.id} />)}
            </div>
            {searchExecuted && numberOfPages && numberOfResults ? (
              <div className={styles.navWrapper} data-test="pagination-nav">
                <Button
                  className={styles.btn}
                  disabled={page === 1}
                  size="small"
                  onClick={(e) => {
                    logAmplitudeEvent('selects the navigation for pagination');
                    changePage(page - 1);
                  }}
                  data-test="page-back"
                >
                  <FontAwesomeIcon icon={faChevronLeft} />
                  Prev
                </Button>

                {new Array(numberOfPages)
                  .fill(null)
                  .slice(0, maxPages)
                  .map((_, i, that) => {
                    let startingIndex = 1;
                    if (page > that.length / 2) {
                      startingIndex = Math.floor(page - that.length / 2 + 1);
                    }
                    if (startingIndex + maxPages > numberOfPages) {
                      startingIndex = numberOfPages - maxPages + 1;
                    }
                    if (startingIndex < 1) {
                      startingIndex = 1;
                    }

                    const I = Math.floor(startingIndex + i);

                    return (
                      <Button
                        active={page === I}
                        size="small"
                        key={I}
                        onClick={(e) => {
                          logAmplitudeEvent('selects the navigation for pagination');
                          changePage(I);
                        }}
                        className={styles.btn}
                        data-test="paginate"
                      >
                        {I}
                      </Button>
                    );
                  })}
                <Button
                  className={styles.btn}
                  disabled={page === numberOfPages}
                  size="small"
                  onClick={(e) => {
                    logAmplitudeEvent('selects the navigation for pagination');
                    changePage(page + 1);
                  }}
                  data-test="page-next"
                >
                  Next
                  <FontAwesomeIcon icon={faChevronRight} />
                </Button>
              </div>
            ) : null}
          </>
        )}
      </div>
    </>
  );
};

export default Agents;
